import { HttpResponse } from "@angular/common/http";
import { OperatorFunction } from "rxjs";
import { map } from "rxjs/operators";

/**
 * Los endpoints que devuelven un bean (no un ConsultaList) marcan el error
 * en los headers: COD_STATUS = ERROR y el motivo en MESSAGE (SelloDeError,
 * en el servidor). Este operador convierte esa marca en un error de rxjs,
 * para que el componente lo atienda en `error:` como cualquier otro.
 *
 * Uso: this.http.post<T>(url, cuerpo, { observe: "response" }).pipe(sinSelloDeError())
 */
export function sinSelloDeError<T>(mensajePorOmision = "No se pudo completar la operación."): OperatorFunction<HttpResponse<T>, T> {
  return map((respuesta: HttpResponse<T>) => {
    if ((respuesta.headers.get("COD_STATUS") || "").toUpperCase() === "ERROR") {
      throw new Error(respuesta.headers.get("MESSAGE") || respuesta.headers.get("IDMESSAGE") || mensajePorOmision);
    }
    return respuesta.body as T;
  });
}

/** Lo que deja una descarga: el archivo, o la constancia de que no había nada que bajar. */
export type ResultadoDescarga = { tipo: "archivo"; blob: Blob } | { tipo: "vacio" };

/**
 * El mismo sello, para descargas pedidas con responseType: "blob". Ahí el
 * cuerpo de un error de negocio llega como un Blob vacío con status 200 y,
 * leído a secas, se confundía con un archivo (o con "no hay datos"). El
 * servidor contesta 204 cuando no hay nada que descargar; eso se devuelve
 * como { tipo: "vacio" } y no como error, porque no lo es.
 *
 * Uso: this.http.get(url, { observe: "response", responseType: "blob" }).pipe(archivoSinSelloDeError())
 */
export function archivoSinSelloDeError(mensajePorOmision = "No se pudo descargar el archivo."): OperatorFunction<HttpResponse<Blob>, ResultadoDescarga> {
  return map((respuesta: HttpResponse<Blob>): ResultadoDescarga => {
    if ((respuesta.headers.get("COD_STATUS") || "").toUpperCase() === "ERROR") {
      throw new Error(respuesta.headers.get("MESSAGE") || respuesta.headers.get("IDMESSAGE") || mensajePorOmision);
    }
    if (respuesta.status === 204) {
      return { tipo: "vacio" };
    }
    if (!respuesta.body || respuesta.body.size === 0) {
      throw new Error(mensajePorOmision);
    }
    return { tipo: "archivo", blob: respuesta.body };
  });
}
